import * as THREE from 'three'
import React, { useMemo } from 'react'
import { extrudeSettings } from '../../../../../../../utils/Functions';

const PatternModel = ({ id, compName, width, height, trimWidth, doorColor, glassTexture }) => {

    const doorWidth = (width - trimWidth * 2) / 2;
    const unitValue = doorWidth / 7 + 0.1
    const gutterDeviation = 0.15;
    const gutterSizeX = unitValue;
    const gutterSizeY = height / 13 + 0.1;
    const roundRadius = unitValue / 5;
    
    const patternTopPosY = height / 2 - trimWidth - gutterSizeY;
    const patternBottomPosY = -height / 2 + gutterSizeY;

    const outerPatternModel = useMemo(() => {
        
        const model = new THREE.Shape();
        // The starting point location is top-mid
        model.moveTo(0, patternTopPosY);
        model.lineTo(doorWidth / 2 - gutterSizeX - roundRadius, patternTopPosY);
        model.quadraticCurveTo(doorWidth / 2 - gutterSizeX, patternTopPosY, doorWidth / 2 - gutterSizeX, patternTopPosY - roundRadius);
        model.lineTo(doorWidth / 2 - gutterSizeX, patternTopPosY - (gutterSizeY * 3 - 0.45));
        model.lineTo(doorWidth / 2 - gutterSizeX - gutterDeviation, patternTopPosY - (gutterSizeY * 3 - 0.45) - gutterDeviation);
        model.lineTo(doorWidth / 2 - gutterSizeX - gutterDeviation, patternBottomPosY + (gutterSizeY * 3 - 0.45) + gutterDeviation);
        model.lineTo(doorWidth / 2 - gutterSizeX, patternBottomPosY + (gutterSizeY * 3 - 0.45));
        model.lineTo(doorWidth / 2 - gutterSizeX, patternBottomPosY + roundRadius);
        model.quadraticCurveTo(doorWidth / 2 - gutterSizeX, patternBottomPosY, doorWidth / 2 - gutterSizeX - roundRadius, patternBottomPosY);
        
        model.lineTo(0, patternBottomPosY);
        
        model.lineTo(-(doorWidth / 2 - gutterSizeX - roundRadius), patternBottomPosY);
        model.quadraticCurveTo(-(doorWidth / 2 - gutterSizeX), patternBottomPosY, -(doorWidth / 2 - gutterSizeX), patternBottomPosY + roundRadius);
        model.lineTo(-(doorWidth / 2 - gutterSizeX), patternBottomPosY + roundRadius);
        model.lineTo(-(doorWidth / 2 - gutterSizeX), patternBottomPosY + (gutterSizeY * 3 - 0.45));
        model.lineTo(-(doorWidth / 2 - gutterSizeX - gutterDeviation), patternBottomPosY + (gutterSizeY * 3 - 0.45) + gutterDeviation);
        model.lineTo(-(doorWidth / 2 - gutterSizeX - gutterDeviation), patternTopPosY - (gutterSizeY * 3 - 0.45) - gutterDeviation);
        model.lineTo(-(doorWidth / 2 - gutterSizeX), patternTopPosY - (gutterSizeY * 3 - 0.45));
        model.lineTo(-(doorWidth / 2 - gutterSizeX), patternTopPosY - roundRadius);
        model.quadraticCurveTo(-(doorWidth / 2 - gutterSizeX), patternTopPosY, -(doorWidth / 2 - gutterSizeX - roundRadius), patternTopPosY);
        model.lineTo(-(doorWidth / 2 - gutterSizeX - roundRadius), patternTopPosY);

        model.closePath();

        return model
    }, [patternTopPosY, doorWidth, gutterSizeX, roundRadius, gutterSizeY, patternBottomPosY])
    
    const midPatternModel = useMemo(() => {
        const deviation = 0.1;
        
        const model = new THREE.Shape();
        model.moveTo(-(doorWidth / 2 - gutterSizeX - gutterDeviation - deviation), (patternBottomPosY + deviation));
        model.lineTo(-(doorWidth / 2 - gutterSizeX - gutterDeviation - deviation), (patternTopPosY - deviation));
        model.lineTo((doorWidth / 2 - gutterSizeX - gutterDeviation - deviation), (patternTopPosY - deviation));
        model.lineTo((doorWidth / 2 - gutterSizeX - gutterDeviation - deviation), (patternBottomPosY + deviation));
        model.closePath();

        return model;
    
    }, [width, height, trimWidth])

    const innerPatternModel = useMemo(() => {
        const deviation = 0.15;
        
        const model = new THREE.Shape();
        model.moveTo(-(doorWidth / 2 - gutterSizeX - gutterDeviation - deviation), (patternBottomPosY + deviation));
        model.lineTo(-(doorWidth / 2 - gutterSizeX - gutterDeviation - deviation), (patternTopPosY - deviation));
        model.lineTo((doorWidth / 2 - gutterSizeX - gutterDeviation - deviation), (patternTopPosY - deviation));
        model.lineTo((doorWidth / 2 - gutterSizeX - gutterDeviation - deviation), (patternBottomPosY + deviation));
        model.closePath();

        return model;
    
    }, [width, height, trimWidth])

    return (
        <>
            {new Array(2).fill('').map((_, index) =>
                <mesh key={`outer-pattern-model-${index}`} position={[Math.pow(-1, index) * doorWidth / 2, 0, 0.05]}>
                    <extrudeGeometry args={[outerPatternModel, extrudeSettings(0.2, 0.03, 0.03, 0, 2)]} />
                    <meshStandardMaterial color={doorColor} side={THREE.DoubleSide} metalness={0.2} />
                </mesh>
            )}
            {new Array(2).fill('').map((_, index) =>
                <mesh key={`mid-pattern-model-${index}`} position={[Math.pow(-1, index) * doorWidth / 2, 0, 0.025]}>
                    <extrudeGeometry args={[midPatternModel, extrudeSettings(0.25, 0.03, 0.03, 0, 2)]} />
                    <meshStandardMaterial color={doorColor} side={THREE.DoubleSide} metalness={0.4} />
                </mesh>
            )}
            {new Array(2).fill('').map((_, index) =>
                <mesh key={`inner-pattern-model-${index}`} position={[Math.pow(-1, index) * doorWidth / 2, 0, 0]}>
                    <extrudeGeometry args={[innerPatternModel, extrudeSettings(0.3, 0.03, 0.03, 0, 2)]} />
                    <meshStandardMaterial color={doorColor} side={THREE.DoubleSide} />
                </mesh>
            )}
        </>
    )
}

export default PatternModel